import { Component } from 'react'
import * as styles from 'components/features/socialFeed/FeedfilterPopup/FeedfilterPopup.styl'
import * as cn from 'classnames'
import { SidebarPopupBackButton } from 'components/features/pfm/SidebarPopup/SidebarPopupBackButton'
import { BlockCorners } from 'interfaces/component'
import { PopupOverview } from 'components/features/pfm/SidebarPopup/PopupOverview'
import Button, { ButtonProps } from 'components/features/pfm/Button/Button'
import { PeergroupRelation } from 'interfaces/peergroups'
import { FeedTagName } from 'interfaces/feed'
import { PaginationLinks } from 'interfaces/table'
import Deferred from 'components/utils/Deferred'
import Spinner from 'components/features/pfm/Spinner/Spinner'
import { SidebarPopupWithList } from 'components/features/pfm/SidebarPopup/SidebarPopupWithList'
import { SidebarPopupHeader } from 'components/features/pfm/SidebarPopup/SidebarPopupHeader'
import { SidebarPopupFooter } from 'components/features/pfm/SidebarPopup/SidebarPopupFooter'
import { InfiniteScrollable } from 'components/features/pfm/InfiniteScroll/InfiniteScrollable'
import { FollowedTagsItem } from 'components/features/socialFeed/FeedfilterPopup/FollowedTagsItem'

export type FeedFilterTagsPopupStateProps = {
  hidePopupTimeoutId: number | null
  peersRelation: PeergroupRelation | null
  shouldResetOnUnmount: boolean

  hasMore: boolean
  isLoading: boolean
  followedTagNames: FeedTagName[]
  paginationLinks: PaginationLinks | null
  errorCode: number | null
}

export type FeedFilterTagsPopupDispatchProps = {
  onLoadMore: (links: PaginationLinks | null) => void
  onResetFollowedTags: () => void

  onViewPosts: () => void
  onExplorePosts: (relation: PeergroupRelation) => void

  onHidePopup: () => void
  onRegisterHideTimeout: (timeoutId: number) => void
}

export type FeedFilterTagsPopupOwnProps = {
  corners: BlockCorners
  fill: boolean
  onBackButtonClick?: () => void
}

type FeedFilterTagsPopupProps =
  & FeedFilterTagsPopupStateProps
  & FeedFilterTagsPopupDispatchProps
  & FeedFilterTagsPopupOwnProps

const DeferredSpinner = Deferred(
  () => <div className={ styles.preloader }><Spinner /></div>,
  300,
)

export class FeedFilterTagsPopup extends Component<FeedFilterTagsPopupProps> {

  render () {
    const {
      corners, fill,
      onBackButtonClick,
      hidePopupTimeoutId, onRegisterHideTimeout, onHidePopup,
      hasMore, followedTagNames,
    } = this.props

    const isEmpty = !hasMore && followedTagNames.length === 0

    return (
      <SidebarPopupWithList
        fill={ fill }
        corners={ corners }
        hidePopupTimeoutId={ hidePopupTimeoutId }
        onRegisterHideTimeout={ onRegisterHideTimeout }
        onHidePopup={ onHidePopup }
        isEmptyState={ isEmpty }
        header={
          <SidebarPopupHeader
            title='Tags You Follow'
            left={
              !!onBackButtonClick && (
                <SidebarPopupBackButton onClick={ onBackButtonClick } />
              )
            }
          />
        }
        footer={
          <SidebarPopupFooter>
            { this.renderFooter(isEmpty) }
          </SidebarPopupFooter>
        }
      >
        { isEmpty ? this.renderEmptyOverview() : this.renderList() }
      </SidebarPopupWithList>
    )
  }

  componentWillUnmount () {
    const { shouldResetOnUnmount, onResetFollowedTags } = this.props
    if (shouldResetOnUnmount) {
      onResetFollowedTags()
    }
  }

  private renderFooter (isEmpty: boolean) {
    const { onViewPosts } = this.props

    const props: ButtonProps = {
      size: 'small',
      theme: 'blue',
      fullWidth: true,
    }

    const onClick = isEmpty ? this.handleExplorePosts : onViewPosts

    return (
      <div className={ cn({ [styles.buttonWrapper]: !isEmpty }) }>
        <Button { ...props } onClick={ onClick }>
          { isEmpty ? 'Explore' : 'View' } Posts
        </Button>
      </div>
    )
  }

  private renderList () {
    const {
      hasMore,
      isLoading,
      followedTagNames,
      errorCode,
      fill,
    } = this.props

    return (
      <div className={ cn(styles.scrollableWrapper, fill && styles.fill) }>
        <InfiniteScrollable
          allowOuterScroll={ false }
          contentClassName={ styles.scrollable }
          // when it was errored, load users by button click
          hasMore={ hasMore && errorCode === null }
          isLoading={ isLoading }
          onLoadMore={ this.handleLoadMore }
          threshold={ 200 }
        >
          { followedTagNames.map((tagName) => (
            <div key={ tagName } className={ styles.item }>
              <FollowedTagsItem type='tag' name={ tagName } />
            </div>
          )) }
          { isLoading && (
            <DeferredSpinner />
          )}
          { errorCode !== null && !isLoading && (
            <button
              className={ styles.loadMore }
              disabled={ isLoading }
              onClick={ this.handleLoadMore }
            >
              Load more
            </button>
          )}
        </InfiniteScrollable>
      </div>
    )
  }

  private renderEmptyOverview () {
    return (
      <PopupOverview
        imageSrc='/images/emoji/tag.png'
        title={
          <div>
            Looks like you're not
            <br />
            following any tags.
          </div>
        }
        subtitle='Explore your feed to discover more.'
      />
    )
  }

  private handleLoadMore = () => {
    const { onLoadMore, paginationLinks } = this.props
    onLoadMore(paginationLinks)
  }

  private handleExplorePosts = () => {
    const { onExplorePosts, peersRelation } = this.props

    if (peersRelation) {
      onExplorePosts(peersRelation)
    }
  }
}
